import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getUserSearchHistory, deleteSearchEntry } from '../services/searchHistoryService';
import Spinner from '../components/Spinner';
import '../styles/SearchHistoryPage.css';

const SearchHistoryPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchHistory, setSearchHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (user?.userId) {
      loadSearchHistory();
    }
  }, [user?.userId]);

  const loadSearchHistory = async () => {
    try {
      setLoading(true);
      const history = await getUserSearchHistory(user?.userId);
      setSearchHistory(history);
      setError('');
    } catch (error) {
      console.error('Failed to load search history:', error);
      setError('Failed to load search history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReanalyze = (search) => {
    // Navigate to FormPage with the property address
    navigate('/form', {
      state: {
        address: search.propertyAddress,
        isGuest: false,
        isReanalysis: true
      }
    });
  };

  const handleDeleteSearch = async (searchTimestamp) => {
    if (!window.confirm('Are you sure you want to delete this search from your history?')) {
      return;
    }

    try {
      setDeleting(searchTimestamp);
      await deleteSearchEntry(user?.userId, searchTimestamp);
      // Remove from local state
      setSearchHistory(prev => prev.filter(search => search.searchTimestamp !== searchTimestamp));
      setError('');
    } catch (error) {
      console.error('Failed to delete search:', error);
      setError('Failed to delete search. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(parseInt(timestamp));
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  if (!user) {
    return (
      <div className="search-history-page">
        <div className="auth-required">
          <h2>Sign In Required</h2>
          <p>Please sign in to view your search history.</p>
          <button onClick={() => navigate('/login')} className="btn-primary">
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="search-history-page">
      <div className="page-header">
        <h1>Search History</h1>
        <p>Review your previous property analyses</p>
      </div>

      {loading && (
        <div className="loading-container">
          <Spinner size="medium" message="Loading search history..." />
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
          <button onClick={loadSearchHistory} className="retry-btn">
            Retry
          </button>
        </div>
      )}

      {!loading && !error && (
        <div className="search-history-content">
          {searchHistory.length === 0 ? (
            <div className="no-history">
              <div className="no-history-icon">🔍</div>
              <h3>No Search History</h3>
              <p>You haven't analyzed any properties yet.</p>
              <button 
                onClick={() => navigate('/')} 
                className="btn-primary"
              >
                Start Analyzing Properties
              </button>
            </div>
          ) : (
            <>
              <div className="history-stats">
                <div className="stat">
                  <span className="stat-number">{searchHistory.length}</span>
                  <span className="stat-label">Properties Analyzed</span>
                </div>
              </div>

              <div className="search-list">
                {searchHistory.map((search) => (
                  <div key={search.searchTimestamp} className="search-item">
                    <div className="search-header">
                      <div className="property-info">
                        <h3 className="property-address">{search.propertyAddress || 'Unknown Address'}</h3>
                        <span className="search-date">{formatDate(search.searchTimestamp)}</span>
                      </div>
                      <div className="search-actions">
                        <button
                          onClick={() => handleReanalyze(search)}
                          className="reanalyze-btn"
                          title="Re-analyze this property"
                        >
                          🔄 Re-analyze
                        </button>
                        <button
                          onClick={() => handleDeleteSearch(search.searchTimestamp)}
                          className="delete-btn"
                          disabled={deleting === search.searchTimestamp}
                          title="Delete from history"
                        >
                          {deleting === search.searchTimestamp ? '⏳' : '🗑️'}
                        </button>
                      </div>
                    </div>

                    {search.propertyDetails && (
                      <div className="property-metrics">
                        <div className="metric">
                          <span className="metric-label">Price</span>
                          <span className="metric-value">{formatCurrency(search.propertyDetails.price)}</span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Cap Rate</span>
                          <span className="metric-value">
                            {search.propertyDetails.capRate ? 
                              parseFloat(search.propertyDetails.capRate).toFixed(2) : 'N/A'}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchHistoryPage;